import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { COMPANY } from "@/data/site";

interface CTASectionProps {
  titleKey: string;
  descKey: string;
  bgColor?: "blue" | "dark";
  buttons?: { labelKey: string; href: string; type: string }[];
}

export default function CTASection({
  titleKey,
  descKey,
  buttons,
  bgColor = "blue",
}: CTASectionProps) {
  const { t } = useLanguage();
  const bgClass = bgColor === "blue" ? "bg-secondary-blue" : "bg-primary-deep";

  const defaultButtons = [
    { labelKey: "sendInquiry", href: "/contact", type: "white" },
    { labelKey: "callWhatsapp", href: COMPANY.whatsapp, type: "whatsapp" },
    { labelKey: "viewLocation", href: COMPANY.googleMaps, type: "secondary" },
  ];

  const ctaButtons = buttons || defaultButtons;

  return (
    <section className={`${bgClass} section-padding px-4 sm:px-6 lg:px-8`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="content-container text-center"
      >
        <h2 className="font-display font-bold text-2xl md:text-3xl lg:text-4xl text-white leading-tight">
          {t(titleKey)}
        </h2>
        <p className="mt-4 text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
          {t(descKey)}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          {ctaButtons.map((btn) => {
            if (btn.type === "whatsapp") {
              return (
                <a
                  key={btn.href}
                  href={btn.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-success text-white font-body font-semibold text-[0.9375rem] transition-all duration-300 hover:bg-green-600 hover:shadow-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t(btn.labelKey)}
                </a>
              );
            }
            if (btn.type === "secondary" && btn.href.startsWith("http")) {
              return (
                <a
                  key={btn.href}
                  href={btn.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 rounded-md border border-white/30 text-white bg-transparent font-body font-semibold text-[0.9375rem] transition-all duration-300 hover:bg-white/10"
                >
                  {t(btn.labelKey)}
                </a>
              );
            }
            if (btn.type === "white") {
              return (
                <Link
                  key={btn.href}
                  to={btn.href}
                  className="inline-flex items-center px-6 py-3 rounded-md bg-white text-primary-deep font-body font-semibold text-[0.9375rem] transition-all duration-300 hover:bg-gray-100"
                >
                  {t(btn.labelKey)}
                </Link>
              );
            }
            return (
              <Link
                key={btn.href}
                to={btn.href}
                className="inline-flex items-center px-6 py-3 rounded-md border border-white/30 text-white bg-transparent font-body font-semibold text-[0.9375rem] transition-all duration-300 hover:bg-white/10"
              >
                {t(btn.labelKey)}
              </Link>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}
